import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Table, Checkbox, Badge, Text, Card, Group, Stack, Box } from '@mantine/core';
import { useDevice } from '../../hooks/useDevice';
import { EssaySummaryRow } from '../../hooks/useEssayApi';

interface Props {
  rows: EssaySummaryRow[];
  selectedIds: Set<string>;
  onToggle: (userId: string) => void;
  onToggleAll: () => void;
  onOpen: (row: EssaySummaryRow) => void;
  /**
   * Показывать ли колонку отбора. Квартальные отчёты в сводный файл не
   * собираются, и галочки там были бы кнопкой, которая ничего не делает.
   */
  hasSelection?: boolean;
}

const STATUS_COLORS: Record<EssaySummaryRow['status'], string> = {
  submitted: 'teal',
  draft: 'yellow',
  none: 'gray',
};

/** Пустое эссе и его отсутствие одинаково бессмысленны в отчёте. */
export const isSelectable = (row: EssaySummaryRow): boolean =>
  row.status !== 'none' && !row.isEmpty;

/**
 * «Вернули на доработку» и «сам не досдал» — для читающего разные вещи, а статус
 * у обоих один и тот же «черновик». Показываем возврат отдельным состоянием.
 */
function StatusBadge({ row }: { row: EssaySummaryRow }) {
  const { t } = useTranslation();
  if (row.returnedAt && row.status === 'draft') {
    return (
      <Badge variant="light" color="orange">
        {t('rework.summaryStatus')}
      </Badge>
    );
  }
  return (
    <Badge variant="light" color={STATUS_COLORS[row.status]}>
      {t(`essays.status.${row.status}`)}
    </Badge>
  );
}

export const EssaySummaryTable: React.FC<Props> = ({
  rows,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpen,
  hasSelection: showSelection = true,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDevice() === 'mobile';
  const selectableRows = rows.filter(isSelectable);
  const isAllSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.userId));
  const isSomeSelected = selectableRows.some((r) => selectedIds.has(r.userId));

  const updatedLabel = (row: EssaySummaryRow) =>
    row.updatedAt
      ? dayjs(row.updatedAt)
          .locale(i18n.language || 'ru')
          .format('D MMM, HH:mm')
      : '—';

  if (isMobile) {
    return (
      <Stack gap="sm">
        {rows.map((row) => (
          <Card key={row.userId} withBorder radius="md" padding="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              {showSelection && (
                <Checkbox
                  checked={selectedIds.has(row.userId)}
                  disabled={!isSelectable(row)}
                  onChange={() => onToggle(row.userId)}
                  aria-label={row.name}
                />
              )}
              <Box
                style={{ flex: 1, cursor: row.essayId ? 'pointer' : 'default' }}
                onClick={() => row.essayId && onOpen(row)}
              >
                <Text fw={600}>{row.name}</Text>
                {row.position && (
                  <Text size="xs" c="dimmed">
                    {row.position}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {updatedLabel(row)}
                </Text>
              </Box>
              <StatusBadge row={row} />
            </Group>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Table highlightOnHover verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          {showSelection && (
            <Table.Th w={40}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected && !isAllSelected}
                disabled={selectableRows.length === 0}
                onChange={onToggleAll}
                aria-label={t('essays.compilePdf')}
              />
            </Table.Th>
          )}
          <Table.Th>{t('essays.columnPerson')}</Table.Th>
          <Table.Th w={140}>{t('essays.columnStatus')}</Table.Th>
          <Table.Th w={160}>{t('essays.columnUpdated')}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={row.userId}>
            {showSelection && (
              <Table.Td>
                <Checkbox
                  checked={selectedIds.has(row.userId)}
                  disabled={!isSelectable(row)}
                  onChange={() => onToggle(row.userId)}
                  aria-label={row.name}
                />
              </Table.Td>
            )}
            <Table.Td
              style={{ cursor: row.essayId ? 'pointer' : 'default' }}
              onClick={() => row.essayId && onOpen(row)}
            >
              <Text fw={500}>{row.name}</Text>
              {row.position && (
                <Text size="xs" c="dimmed">
                  {row.position}
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <StatusBadge row={row} />
            </Table.Td>
            <Table.Td>
              <Text size="sm" c="dimmed">
                {updatedLabel(row)}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
